'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '../../toast/ToastProvider';
import type {
  ApplicationDocumentationController,
  ApplicationDocumentationFile,
  DocumentationDeleteTarget,
  DocumentationFileCategory,
} from './applicationDocumentation.types';
import { resolveDocumentationDeleteSection } from './applicationDocumentationFiles.utils';
import type { PersistDocumentationUrls } from './usePersistDocumentationUrls';
import type { StagedDocumentationState } from './useStagedDocumentation';

export type DocumentationDeleteState = {
  pendingDelete: DocumentationDeleteTarget | null;
  isDeletingItem: boolean;
  deleteSpinnerSection: DocumentationFileCategory | null;
  omitFileRowId: string | null;
  omitUrlRowIndex: number | null;
  requestRemoveFile: ({
    category,
    fileIndex,
  }: {
    category: DocumentationFileCategory;
    fileIndex: number;
  }) => void;
  requestRemoveUrl: ({ urlIndex }: { urlIndex: number }) => void;
  handleDialogOpenChange: (open: boolean) => void;
  confirmDelete: () => Promise<void>;
};

export const useDocumentationDelete = ({
  applicationId,
  isDeferred,
  controller,
  visibleDocumentationUrls,
  persistDocumentationUrls,
  staged,
}: {
  applicationId: string | null;
  isDeferred: boolean;
  controller: ApplicationDocumentationController;
  visibleDocumentationUrls: string[];
  persistDocumentationUrls: PersistDocumentationUrls;
  staged: Pick<
    StagedDocumentationState,
    'stageFileRemoval' | 'stageDocumentationUrls'
  >;
}): DocumentationDeleteState => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const {
    applicationDocumentationFiles,
    apiDocumentationFiles,
    previousTestingReportsFiles,
    documentationUrls,
    deleteFile,
    isDeletingFile,
    isSavingDocumentationUrls,
  } = controller;
  const { stageFileRemoval, stageDocumentationUrls } = staged;
  const [pendingDelete, setPendingDelete] =
    useState<DocumentationDeleteTarget | null>(null);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [rowDeleteInFlight, setRowDeleteInFlight] =
    useState<DocumentationDeleteTarget | null>(null);
  const filesByCategory = useMemo<
    Record<DocumentationFileCategory, ApplicationDocumentationFile[]>
  >(
    () => ({
      application: applicationDocumentationFiles,
      api: apiDocumentationFiles,
      'testing-reports': previousTestingReportsFiles,
    }),
    [
      apiDocumentationFiles,
      applicationDocumentationFiles,
      previousTestingReportsFiles,
    ],
  );
  const isBusy =
    rowDeleteInFlight !== null ||
    isDeletingItem ||
    isDeletingFile ||
    isSavingDocumentationUrls;

  const executeDelete = useCallback(
    async (pending: DocumentationDeleteTarget) => {
      if (!applicationId) {
        return;
      }

      setRowDeleteInFlight(pending);
      if (!isDeferred) {
        setIsDeletingItem(true);
      }

      try {
        if (pending.type === 'file') {
          await deleteFile({ applicationId, fileId: pending.fileId });
          if (!isDeferred) {
            toasterApi.success({
              description: t('itemDeletedSuccessfully', {
                item: t('fileLabel'),
              }),
            });
            setPendingDelete(null);
          }

          return;
        }

        const didSave = await persistDocumentationUrls({
          urls: documentationUrls.filter(
            (_, currentIndex) => currentIndex !== pending.urlIndex,
          ),
        });

        if (didSave && !isDeferred) {
          toasterApi.success({
            description: t('itemDeletedSuccessfully', {
              item: t('linkLabel'),
            }),
          });
          setPendingDelete(null);
        }
      } catch (error) {
        console.error(
          new Error('Failed to delete documentation item', {
            cause: error instanceof Error ? error : new Error(String(error)),
          }),
        );
        toasterApi.error({
          title: t('errors.failedToDeleteFile'),
          description: t('errors.pleaseTryAgain'),
        });
      } finally {
        setRowDeleteInFlight(null);
        if (!isDeferred) {
          setIsDeletingItem(false);
        }
      }
    },
    [
      applicationId,
      deleteFile,
      documentationUrls,
      isDeferred,
      persistDocumentationUrls,
      t,
    ],
  );

  const requestRemoveFile = useCallback(
    ({
      category,
      fileIndex,
    }: {
      category: DocumentationFileCategory;
      fileIndex: number;
    }) => {
      if (!applicationId || isBusy) {
        return;
      }

      const files = filesByCategory[category];

      if (isDeferred) {
        stageFileRemoval({ category, fileIndex, files });

        return;
      }

      const fileId = files[fileIndex]?.id;

      if (fileId) {
        setPendingDelete({ type: 'file', fileId });
      }
    },
    [applicationId, filesByCategory, isBusy, isDeferred, stageFileRemoval],
  );

  const requestRemoveUrl = useCallback(
    ({ urlIndex }: { urlIndex: number }) => {
      if (isBusy) {
        return;
      }

      if (isDeferred) {
        stageDocumentationUrls(
          visibleDocumentationUrls.filter(
            (_, currentIndex) => currentIndex !== urlIndex,
          ),
        );

        return;
      }

      setPendingDelete({ type: 'link', urlIndex });
    },
    [isBusy, isDeferred, stageDocumentationUrls, visibleDocumentationUrls],
  );

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isDeletingItem) {
        setPendingDelete(null);
      }
    },
    [isDeletingItem],
  );

  const confirmDelete = useCallback(async () => {
    if (pendingDelete) {
      await executeDelete(pendingDelete);
    }
  }, [executeDelete, pendingDelete]);

  return {
    pendingDelete,
    isDeletingItem,
    deleteSpinnerSection: resolveDocumentationDeleteSection({
      target: rowDeleteInFlight,
      filesByCategory,
    }),
    omitFileRowId:
      rowDeleteInFlight?.type === 'file' ? rowDeleteInFlight.fileId : null,
    omitUrlRowIndex:
      rowDeleteInFlight?.type === 'link' ? rowDeleteInFlight.urlIndex : null,
    requestRemoveFile,
    requestRemoveUrl,
    handleDialogOpenChange,
    confirmDelete,
  };
};
